// src/composables/useAutoLogout.js
// ✅ 자동 로그아웃 Composable
// - VueUse useIdle 기반 유휴 감지
// - 경고 모달 + 카운트다운
// - 탭 간 동기화

import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useIdle, useTimestamp, useBroadcastChannel } from '@vueuse/core';
import { useRouter } from 'vue-router';

import { useMainStore } from '@/store/mainStore';
import { useModalStore } from '@/store/modalStore';
import loginApi from '@/common/api/loginApi';
import userUtils from '@/common/userUtils';
import { logDebug, logInfo, logWarn } from '@/utils/logHelper';

/* =========================================================
 * 상수 설정
 * ======================================================= */

// ✅ 환경변수 또는 기본값 (밀리초)
const IDLE_TIMEOUT = Number(process.env.VUE_APP_IDLE_TIMEOUT) || 30 * 60 * 1000;  // 30분
const WARNING_BEFORE = Number(process.env.VUE_APP_IDLE_WARNING) || 5 * 60 * 1000;  // 5분 전 경고
const COUNTDOWN_INTERVAL = 1000;  // 1초

// 감지할 이벤트 목록
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'wheel',
];

// 탭 간 동기화 채널
const CHANNEL_NAME = 'mpnite-session-sync';

/**
 * 자동 로그아웃 Composable
 * 
 * @param {Object} options
 * @param {number} options.timeout - 유휴 타임아웃 (ms), 기본 30분
 * @param {number} options.warningBefore - 경고 표시 시점 (ms), 기본 5분 전
 * @param {boolean} options.syncTabs - 탭 간 동기화 여부, 기본 true
 * @param {Function} options.onWarning - 경고 시 콜백
 * @param {Function} options.onLogout - 로그아웃 시 콜백
 * 
 * @example
 * // App.vue 또는 레이아웃 컴포넌트에서
 * const {
 *   isIdle,
 *   isWarningVisible,
 *   remainingTime,
 *   remainingFormatted,
 *   extendSession,
 *   logoutNow,
 * } = useAutoLogout({
 *   timeout: 30 * 60 * 1000,  // 30분
 *   warningBefore: 5 * 60 * 1000,  // 5분 전 경고
 * });
 */
export function useAutoLogout(options = {}) {
  const {
    timeout = IDLE_TIMEOUT,
    warningBefore = WARNING_BEFORE,
    syncTabs = true,
    onWarning = null,
    onLogout = null,
  } = options;

  /* =========================================================
   * 의존성
   * ======================================================= */
  const router = useRouter();
  const mainStore = useMainStore();
  const modalStore = useModalStore();

  /* =========================================================
   * 반응형 상태
   * ======================================================= */
  const isEnabled = ref(false);
  const isWarningVisible = ref(false);
  const remainingTime = ref(0);  // 남은 시간 (ms)
  const lastActivityTime = ref(Date.now());

  // VueUse useIdle - 유휴 상태 감지
  const { idle: isIdle, lastActive } = useIdle(timeout, {
    events: ACTIVITY_EVENTS,
    initialState: false,
  });

  // 탭 간 동기화 (BroadcastChannel)
  const { post: broadcastPost, data: broadcastData } = syncTabs
    ? useBroadcastChannel({ name: CHANNEL_NAME })
    : { post: () => {}, data: ref(null) };

  // 카운트다운 타이머
  let countdownTimer = null;
  let checkTimer = null;

  /* =========================================================
   * Computed
   * ======================================================= */

  // 남은 시간 포맷팅 (MM:SS)
  const remainingFormatted = computed(() => {
    const totalSeconds = Math.max(0, Math.floor(remainingTime.value / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  });

  // 경고 시작 시점 (타임아웃 - 경고 시간)
  const warningStartTime = computed(() => timeout - warningBefore);

  // 로그인 상태
  const isLoggedIn = computed(() => mainStore.isLogin);

  /* =========================================================
   * 내부 함수
   * ======================================================= */

  /**
   * 세션 타이머 시작
   */
  function startSessionTimer() {
    if (!isLoggedIn.value) return;

    lastActivityTime.value = Date.now();
    isEnabled.value = true;

    // 주기적 체크 (1초마다)
    stopTimers();
    checkTimer = setInterval(checkSessionStatus, COUNTDOWN_INTERVAL);

    logDebug('[AutoLogout] 세션 타이머 시작', {
      timeout: `${timeout / 60000}분`,
      warningBefore: `${warningBefore / 60000}분`,
    });
  }

  /**
   * 세션 상태 체크
   */
  function checkSessionStatus() {
    if (!isEnabled.value || !isLoggedIn.value) {
      stopTimers();
      return;
    }

    const now = Date.now();
    const elapsed = now - lastActive.value;
    const remaining = timeout - elapsed;

    remainingTime.value = Math.max(0, remaining);

    // 타임아웃 도달 → 로그아웃
    if (remaining <= 0) {
      handleAutoLogout();
      return;
    }

    // 경고 시점 도달
    if (remaining <= warningBefore && !isWarningVisible.value) {
      showWarning();
    }

    // 활동 감지 시 경고 숨김
    if (remaining > warningBefore && isWarningVisible.value) {
      hideWarning();
    }
  }

  /**
   * 경고 표시
   */
  function showWarning() {
    if (isWarningVisible.value) return;

    isWarningVisible.value = true;
    logInfo('[AutoLogout] 세션 만료 경고 표시');

    // 콜백 호출
    onWarning?.();

    // 모달 표시 (modalStore 사용 시)
    try {
      modalStore?.openModal?.('SESSION_WARNING');
    } catch (e) {
      // modalStore 없으면 무시
    }

    // 탭 간 동기화
    broadcastPost({ type: 'SESSION_WARNING', timestamp: Date.now() });
  }

  /**
   * 경고 숨김
   */
  function hideWarning() {
    if (!isWarningVisible.value) return;

    isWarningVisible.value = false;

    try {
      modalStore?.closeModal?.('SESSION_WARNING');
    } catch (e) {
      // 무시
    }
  }


/**
 * 자동 로그아웃 실행
 */
async function handleAutoLogout() {
  logWarn('[AutoLogout] 세션 만료 - 자동 로그아웃');

  stopTimers();
  isEnabled.value = false;
  isWarningVisible.value = false;

  onLogout?.();
  broadcastPost({ type: 'SESSION_EXPIRED', timestamp: Date.now() });

  try {
    await loginApi.forceLogout({ clearAllData: true });

    // ✅ 홈으로 이동 (login 라우트가 없으므로)
    router.push({
      name: 'home',
      query: { 
        reason: 'session_expired',
        redirect: router.currentRoute.value.fullPath,
      },
    });
  } catch (e) {
    logWarn('[AutoLogout] 로그아웃 처리 중 오류', e);
    // ✅ fallback도 /으로
    window.location.href = '/?reason=session_expired';
  }
}


  /**
   * 타이머 정지
   */
  function stopTimers() {
    if (checkTimer) {
      clearInterval(checkTimer);
      checkTimer = null;
    }
    if (countdownTimer) {
      clearInterval(countdownTimer);
      countdownTimer = null;
    }
  }

  /* =========================================================
   * 공개 메서드
   * ======================================================= */

  /**
   * 세션 연장 (활동으로 간주)
   */
  function extendSession() {
    lastActivityTime.value = Date.now();
    hideWarning();
    
    // 탭 간 동기화
    broadcastPost({ type: 'SESSION_EXTENDED', timestamp: Date.now() });

    logInfo('[AutoLogout] 세션 연장됨');
  }

  /**
   * 즉시 로그아웃
   */
  async function logoutNow() {
    logInfo('[AutoLogout] 사용자 요청 로그아웃');
    
    stopTimers();
    isEnabled.value = false;
    isWarningVisible.value = false;

    // 탭 간 동기화
    broadcastPost({ type: 'USER_LOGOUT', timestamp: Date.now() });

    await loginApi.logout({ clearAllData: true });

    router.push({ name: 'login' });
  }

  /**
   * 자동 로그아웃 일시 중지
   */
  function pause() {
    isEnabled.value = false;
    stopTimers();
    logDebug('[AutoLogout] 일시 중지');
  }

  /**
   * 자동 로그아웃 재개
   */
  function resume() {
    if (isLoggedIn.value) {
      startSessionTimer();
      logDebug('[AutoLogout] 재개');
    }
  }

  /**
   * 남은 시간 조회 (디버깅용)
   */
  function getStatus() {
    return {
      isEnabled: isEnabled.value,
      isIdle: isIdle.value,
      isWarningVisible: isWarningVisible.value,
      remainingTime: remainingTime.value,
      remainingFormatted: remainingFormatted.value,
      lastActive: new Date(lastActive.value).toISOString(),
    };
  }

  /* =========================================================
   * Watchers
   * ======================================================= */

  // 로그인 상태 변화 감지
  watch(isLoggedIn, (loggedIn) => {
    if (loggedIn) {
      startSessionTimer();
    } else {
      stopTimers();
      isEnabled.value = false;
      isWarningVisible.value = false;
    }
  }, { immediate: true });

  // 탭 간 메시지 수신
  watch(broadcastData, (message) => {
    if (!message || !isEnabled.value) return;

    logDebug('[AutoLogout] 탭 간 메시지 수신:', message);

    switch (message.type) {
      case 'SESSION_EXTENDED':
        // 다른 탭에서 세션 연장됨
        lastActivityTime.value = message.timestamp;
        hideWarning();
        break;

case 'SESSION_EXPIRED':
case 'USER_LOGOUT':
  stopTimers();
  isEnabled.value = false;
  isWarningVisible.value = false;
  mainStore.SET_IS_LOGIN(false);
  // ✅ 홈으로 변경
  router.push({ 
    name: 'home', 
    query: { reason: 'logged_out_other_tab' } 
  });
  break;

      case 'SESSION_WARNING':
        // 다른 탭에서 경고 표시
        if (!isWarningVisible.value) {
          showWarning();
        }
        break;
    }
  });

  /* =========================================================
   * Lifecycle
   * ======================================================= */

  onMounted(() => {
    if (isLoggedIn.value) {
      startSessionTimer();
    }
  });

  onUnmounted(() => {
    stopTimers();
  });

  /* =========================================================
   * Return
   * ======================================================= */

  return {
    // 상태
    isEnabled,
    isIdle,
    isWarningVisible,
    remainingTime,
    remainingFormatted,
    lastActive,

    // 메서드
    extendSession,
    logoutNow,
    pause,
    resume,
    getStatus,

    // 설정값 (읽기 전용)
    timeout,
    warningBefore,
  };
}

export default useAutoLogout;